import { useMutation, useQuery, useQueryClient } from "react-query"
import { toast } from "react-toastify"
import { useDispatch, useSelector } from "react-redux"
import { t } from "i18next"
import { useEffect, useState } from "react"
import { get, isNumber } from 'lodash'
import { TRider } from "../shared/types/rider.type"
import { Riders } from "../api/riders"
import { TrackName } from "../api/trackName"
import { Ttrack, trackSurface } from "../shared/types/tracks.type"
import { TFinishLine } from "../shared/types/finishLine.type"
import { useAuth } from "./useAuth"
import { AxiosError } from "axios"
import { RootState } from "../store"
import { TUser } from "../shared/types/user.type"
import { TTrainingType } from "../shared/types/training.type"
import { TPublicTrack } from "../shared/types/publicTrack.type"
import { hydraTransform } from "../shared/utils/hydraTransformer"
import { setTrackLibraryModal } from "../store/reducers/modal.reducer"
export const useTracks = (enableQuery: boolean, trainer?: number, allAvailable: boolean = false, enableTrainingTypesQuery = false) => {
    const dispach = useDispatch()
    const queryClient = useQueryClient()
    const User = useSelector<RootState>(state => state.user.user) as TUser

    //State
    const [publicTracks, setPublicTracks] = useState<TPublicTrack[]>([])
    //Mutations
    const saveTrackMutation = useMutation((data: Partial<Ttrack>) => TrackName.save(data))
    const saveFLMutation = useMutation((data: Partial<TFinishLine>) => TrackName.saveFinishLine(data))
    const deleteTrackMutation = useMutation((id: number) => TrackName.delete(id))
    const deleteFLMutation = useMutation((id: number) => TrackName.deleteFL(id))
    const getTrackByIdMutation = useMutation((id: number) => TrackName.getTrack(id))
    const updateTrackMutation = useMutation((data: { id: number, finish_line: string } | Partial<Ttrack>) => TrackName.update(data))
    const getTracksSurfacesMutation = useMutation(() => TrackName.getSuraces())
    const getPublicTrackMutation = useMutation(() => TrackName.getPublicTracks())
    const savePublicTracksMutation = useMutation((data: { public_tracks: string[] }) => TrackName.savePublicTracks(User.id, data))


    const query = useQuery(['tracksNames'], () => TrackName.getTracks(trainer, allAvailable), {
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        enabled: enableQuery,

    })

    const trainingsTypesQuery = useQuery(['training_types'], () => TrackName.getTrainingTypes(User?.["@id"], allAvailable), {
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        enabled: (enableQuery && enableTrainingTypesQuery),

    })

    const getTrackSurfaces = async (): Promise<Array<trackSurface> | []> => {
        try {
            let response = await getTracksSurfacesMutation.mutateAsync()

            return get(response, 'data.hydra:member', [])
        } catch (err) {
            console.log({ err });

            return []
        }
    }

    const getTrackById = async (id: number): Promise<Ttrack | null> => {
        try {
            let response = await getTrackByIdMutation.mutateAsync(id)

            return response.data
        } catch (err) {
            console.log({ err });

            return null
        }
    }

    const saveTrack = async (data: Partial<Ttrack>) => {
        try {
            let response = await saveTrackMutation.mutateAsync(data)

            queryClient.refetchQueries(['tracksNames'])

            return response.data
        } catch (error) {
            toast(t('error:generic'), { type: 'error' })

        }

    }

    const saveFL = async (data: Partial<TFinishLine>) => {
        try {
            let response = await saveFLMutation.mutateAsync(data)
            return response.data
        } catch (error) {
            toast(t('error:generic'), { type: 'error' })

        }
    }

    const updateTrack = async (data: { id: number, finish_line: string } | Partial<Ttrack>) => {

        try {
            let response = await updateTrackMutation.mutateAsync(data)

            toast(t('defaultSuccessMessage'), { type: 'success' })

            queryClient.refetchQueries(['tracksNames'])
            return response.data
        } catch (error) {
            toast(t('error:generic'), { type: 'error' })
        }
    }

    const deleteFinishLine = (id: number) => {

        return deleteFLMutation.mutateAsync(id)
            .then(({ data }) => {
                // dispach(seTRiderModal(false))
                queryClient.refetchQueries(['tracksNames'])
                return data
            }).catch(err => {
                toast(t('error:generic'), { type: 'error' })
                return err
            })

    }

    const deleteTrack = (id: number) => {
        return deleteTrackMutation.mutateAsync(id)
            .then(({ data }) => {
                // dispach(seTRiderModal(false))
                queryClient.refetchQueries(['tracksNames'])
                return data
            }).catch(err => {
                toast(t('error:generic'), { type: 'error' })
                return err
            })

    }

    const getPublicTracks = async () => {
        try {
            let response = await getPublicTrackMutation.mutateAsync()
            let data = hydraTransform(response.data)
            setPublicTracks(data)
        } catch (err) {
            toast(t('error:generic'), { type: 'error' })
            return err
        }
    }

    const savePublicTracks = async (ids: string[]) => {
        return savePublicTracksMutation.mutateAsync({ public_tracks: ids })
            .then(result => {
                toast(t('defaultSuccessMessage'), { type: 'success' })
                queryClient.refetchQueries(['tracksNames'])
                return result
            }).catch(err => {
                toast(t('error:generic'), { type: 'error' })
                return err
            })
    }



    return {
        saveTrack,
        updateTrack,
        deleteTrack,
        getTrackSurfaces,
        getTrackById,
        saveFL,
        deleteFinishLine,
        isDeletingFL: deleteFLMutation.isLoading,
        isTrackLoading: getTrackByIdMutation.isLoading,
        isTrackError: getTrackByIdMutation.isError,
        isDeleting: deleteTrackMutation.isLoading,
        isDeleted: deleteTrackMutation.isSuccess,
        isSaving: saveTrackMutation.isLoading,
        isSavingFL: saveFLMutation.isLoading,
        isSurfacesLoading: getTracksSurfacesMutation.isLoading,
        isUpdating: updateTrackMutation.isLoading,
        tracks: hydraTransform(query.data?.data ?? []),
        isLoading: query.isLoading || query.isFetching,
        trainingTypes: hydraTransform(trainingsTypesQuery.data?.data ?? []),
        isLoadingTrainingTypes: trainingsTypesQuery.isLoading || trainingsTypesQuery.isFetching,
        getPublicTracks,
        publicTracks,
        savePublicTracks,
        publicTracksLoading: getPublicTrackMutation.isLoading,
        isSavingPublicTracks: savePublicTracksMutation.isLoading
    }
}